// frontend/src/components/ApplicationStatus.js
import React, { useEffect, useState, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import api from '../api';
import logoutManager from '../logout'; // Import the logout manager
import { 
  FileText, CheckCircle, XCircle, AlertCircle, Clock, 
  Edit, Upload, Eye, X, 
  Maximize2, Minimize2, Download, ZoomIn, ZoomOut, LogOut
} from 'lucide-react';

const MAIN_DOMAIN = process.env.REACT_APP_MAIN_DOMAIN || 'truyon.com';

const ApplicationStatus = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { userId: stateUserId } = location.state || {};
  const userId = stateUserId || localStorage.getItem('userId');
  
  const [application, setApplication] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [isEditing, setIsEditing] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [reviewMessage, setReviewMessage] = useState('');
  
  // Preview modal state
  const [previewModal, setPreviewModal] = useState({
    isOpen: false,
    documentType: null,
    documentUrl: null,
    documentName: null,
    documentMimeType: null,
    isFullScreen: false
  });
  
  // Zoom state for images
  const [scale, setScale] = useState(1);
  
  // Edit form state
  const [editForm, setEditForm] = useState({
    organizationName: '',
    address: '',
    phone: '',
    email: ''
  });
  
  // Track which documents are being replaced
  const [replaceDocuments, setReplaceDocuments] = useState({
    addressProof: false,
    institutionProof: false
  });
  
  const [newFiles, setNewFiles] = useState({
    addressProof: null,
    institutionProof: null
  });
  
  const [, setFilePreviews] = useState({
    addressProof: null,
    institutionProof: null
  });
  
  const [errors, setErrors] = useState({});
  
  const fetchApplicationStatus = useCallback(async () => {
    try {
      const response = await api.get(`api/application/${userId}`);
      setApplication(response.data);

      setEditForm({
        organizationName: response.data.organizationName || '',
        address: response.data.address || '',
        phone: response.data.phone || '',
        email: response.data.email || ''
      });

      if (response.data.reviewComments) {
        setReviewMessage(response.data.reviewComments);
      }
    } catch (err) {
      if (err.response?.status === 404) {
        setError('No application found for this user.');
      } else {
        setError('Error fetching application status.');
      }
    } finally {
      setLoading(false);
    }
  }, [userId]);

  useEffect(() => {
    if (!userId) {
      setError('User ID is missing. Redirecting to sign-in...');
      setTimeout(() => navigate('/signin'), 3000);
      return;
    }
    fetchApplicationStatus();
  }, [fetchApplicationStatus, navigate, userId]);

  // Updated handleLogout using the logout manager
  const handleLogout = async () => {
    try {
      await logoutManager.logout({
        redirectTo: '/signin',
        showToast: true,
        onSuccess: () => {
          console.log('Logout successful');
        },
        onError: (error) => {
          console.error('Logout error:', error);
        }
      });
    } catch (error) {
      console.error('Error during logout:', error);
      // Fallback logout if the manager fails
      localStorage.clear();
      sessionStorage.clear();
      window.location.href = '/signin';
    }
  };

  const handleEditChange = (e) => {
    const { name, value } = e.target;
    setEditForm(prev => ({ ...prev, [name]: value }));
    if (errors[name]) setErrors(prev => ({ ...prev, [name]: null }));
  };

  const toggleReplaceDocument = (documentType) => {
    setReplaceDocuments(prev => ({
      ...prev,
      [documentType]: !prev[documentType]
    }));
    
    if (replaceDocuments[documentType]) {
      setNewFiles(prev => ({ ...prev, [documentType]: null }));
      setFilePreviews(prev => ({ ...prev, [documentType]: null }));
    }
  };

  const handleFileChange = (e, fileType) => {
    const file = e.target.files[0];
    if (!file) return;

    if (file.size > 5 * 1024 * 1024) {
      setErrors(prev => ({
        ...prev,
        [fileType]: 'File size should be less than 5MB'
      }));
      return;
    }

    const allowedTypes = [
      'image/jpeg', 'image/png', 'image/jpg', 'image/gif', 'image/webp',
      'application/pdf'
    ];
    
    if (!allowedTypes.includes(file.type)) {
      setErrors(prev => ({
        ...prev,
        [fileType]: 'Only images and PDF files are allowed'
      }));
      return;
    }

    setErrors(prev => ({ ...prev, [fileType]: null }));
    setNewFiles(prev => ({ ...prev, [fileType]: file }));

    const reader = new FileReader();
    reader.onloadend = () => {
      setFilePreviews(prev => ({ ...prev, [fileType]: reader.result }));
    };
    reader.readAsDataURL(file);
  };

  const removeNewFile = (fileType) => {
    setNewFiles(prev => ({ ...prev, [fileType]: null }));
    setFilePreviews(prev => ({ ...prev, [fileType]: null }));
    document.getElementById(fileType).value = '';
  };

  const handleSubmitEdit = async () => {
    const newErrors = {};
    if (!editForm.organizationName) newErrors.organizationName = 'Organization name is required';
    if (!editForm.address) newErrors.address = 'Address is required';
    if (!editForm.email) newErrors.email = 'Email is required';
    
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    setIsSubmitting(true);

    try {
      const formPayload = new FormData();
      formPayload.append('organizationName', editForm.organizationName);
      formPayload.append('address', editForm.address);
      formPayload.append('phone', editForm.phone);
      formPayload.append('email', editForm.email);
      
      if (replaceDocuments.addressProof && newFiles.addressProof) {
        formPayload.append('addressProof', newFiles.addressProof);
      }
      if (replaceDocuments.institutionProof && newFiles.institutionProof) {
        formPayload.append('institutionProof', newFiles.institutionProof);
      }

      await api.put(`/api/application/${application._id}`, formPayload, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      await fetchApplicationStatus();
      setIsEditing(false);
      setReplaceDocuments({ addressProof: false, institutionProof: false });
      setNewFiles({ addressProof: null, institutionProof: null });
      setFilePreviews({ addressProof: null, institutionProof: null });
      
    } catch (error) {
      console.error('Error updating application:', error);
      alert(error.response?.data?.message || 'Failed to update application');
    } finally {
      setIsSubmitting(false);
    }
  };

  const previewDocument = async (documentType) => {
    if (!application?.documents?.[documentType]?.fileId) return;
    
    try {
      const document = application.documents[documentType];
      const fileKey = encodeURIComponent(document.fileId);
      const url = `${process.env.REACT_APP_BACKEND_URL || 'http://localhost:5000'}/api/application/document/${fileKey}`;
      
      console.log('Fetching document:', { documentType, url, mimeType: document.mimeType });
      
      const token = localStorage.getItem('token');
      const response = await fetch(url, {
        headers: token ? { 'Authorization': `Bearer ${token}` } : {}
      });
      
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }
      
      const blob = await response.blob();
      const objectUrl = window.URL.createObjectURL(blob);
      
      let mimeType = document.mimeType;
      if (!mimeType && document.fileName) {
        const ext = document.fileName.split('.').pop().toLowerCase();
        const mimeTypes = {
          'pdf': 'application/pdf',
          'jpg': 'image/jpeg',
          'jpeg': 'image/jpeg',
          'png': 'image/png',
          'gif': 'image/gif',
          'webp': 'image/webp'
        };
        mimeType = mimeTypes[ext] || blob.type;
      } else if (!mimeType) {
        mimeType = blob.type;
      }
      
      setPreviewModal({
        isOpen: true,
        documentType,
        documentUrl: objectUrl,
        documentName: document.fileName || documentType,
        documentMimeType: mimeType,
        isFullScreen: false
      });
      
      setScale(1);
      
    } catch (err) {
      console.error('Error loading document:', err);
      alert('Failed to load document');
    }
  };

  const closePreview = () => {
    if (previewModal.documentUrl) {
      window.URL.revokeObjectURL(previewModal.documentUrl);
    }
    setPreviewModal({
      isOpen: false,
      documentType: null,
      documentUrl: null,
      documentName: null,
      documentMimeType: null,
      isFullScreen: false
    });
    setScale(1);
  };

  const toggleFullScreen = () => {
    setPreviewModal(prev => ({ ...prev, isFullScreen: !prev.isFullScreen }));
  };

  const getStatusIcon = (status) => {
    switch(status) {
      case 'Approved': return <CheckCircle className="w-5 h-5" />;
      case 'Rejected': return <XCircle className="w-5 h-5" />;
      case 'Review': return <AlertCircle className="w-5 h-5" />;
      default: return <Clock className="w-5 h-5" />;
    }
  };

  const getStatusColor = (status) => {
    switch(status) {
      case 'Approved': return 'bg-emerald-50 text-emerald-700 border border-emerald-200';
      case 'Rejected': return 'bg-rose-50 text-rose-700 border border-rose-200';
      case 'Review': return 'bg-amber-50 text-amber-700 border border-amber-200';
      default: return 'bg-blue-50 text-blue-700 border border-blue-200';
    }
  };

  const getFileIcon = (fileType) => {
    if (fileType === 'addressProof') return <FileText className="w-5 h-5 text-blue-600" />;
    return <FileText className="w-5 h-5 text-purple-600" />;
  };

  const isPdf = previewModal.documentMimeType === 'application/pdf' || 
                 previewModal.documentName?.toLowerCase().endsWith('.pdf');

  const renderPreviewModal = () => {
    if (!previewModal.isOpen) return null;

    return (
      <div 
        className={`fixed inset-0 z-50 flex items-center justify-center bg-slate-900/90 backdrop-blur-sm p-3 md:p-6`}
        onClick={closePreview}
      >
        <div 
          className={`bg-white rounded-xl shadow-2xl overflow-hidden flex flex-col w-full transition-all duration-300 ${
            previewModal.isFullScreen ? 'max-w-full h-full' : 'max-w-5xl h-[85vh]'
          }`}
          onClick={e => e.stopPropagation()}
        >
          {/* Header */}
          <div className="px-4 py-3 bg-slate-50 border-b border-slate-200 flex justify-between items-center">
            <div className="flex items-center gap-2 min-w-0">
              <FileText className="w-5 h-5 text-slate-500 flex-shrink-0" />
              <p className="font-medium text-slate-800 text-sm md:text-base truncate">
                {previewModal.documentName}
              </p>
            </div>
            <div className="flex items-center gap-1 md:gap-2">
              <button
                onClick={toggleFullScreen}
                className="p-1.5 md:p-2 text-slate-600 hover:text-slate-900 hover:bg-slate-200/60 rounded-lg transition-colors"
                title={previewModal.isFullScreen ? 'Exit Full Screen' : 'Full Screen'}
              >
                {previewModal.isFullScreen ? <Minimize2 className="w-4 h-4 md:w-5 h-5" /> : <Maximize2 className="w-4 h-4 md:w-5 h-5" />}
              </button>
              <a
                href={previewModal.documentUrl}
                download={previewModal.documentName}
                className="p-1.5 md:p-2 text-slate-600 hover:text-slate-900 hover:bg-slate-200/60 rounded-lg transition-colors"
                title="Download file"
              >
                <Download className="w-4 h-4 md:w-5 h-5" />
              </a>
              <button
                onClick={closePreview}
                className="p-1.5 md:p-2 text-slate-600 hover:text-rose-600 hover:bg-rose-50 rounded-lg transition-colors"
              >
                <X className="w-4 h-4 md:w-5 h-5" />
              </button>
            </div>
          </div>
          
          {/* Document Content */}
          <div className="flex-1 overflow-auto bg-slate-100 p-3 md:p-6 flex flex-col items-center">
            {isPdf ? (
              <div className="w-full h-full flex flex-col">
                {/* PDF Controls */}
                <div className="bg-white p-2 mb-3 rounded-lg shadow-sm border border-slate-200 flex items-center justify-center gap-3 self-center">
                  <button
                    onClick={() => setScale(s => Math.max(0.5, s - 0.1))}
                    className="p-1 hover:bg-slate-100 rounded text-slate-600"
                  >
                    <ZoomOut className="w-4 h-4" />
                  </button>
                  <span className="text-xs font-semibold text-slate-700 min-w-12 text-center">{Math.round(scale * 100)}%</span>
                  <button
                    onClick={() => setScale(s => Math.min(2, s + 0.1))}
                    className="p-1 hover:bg-slate-100 rounded text-slate-600"
                  >
                    <ZoomIn className="w-4 h-4" />
                  </button>
                </div>
                
                <iframe
                  src={`${previewModal.documentUrl}#toolbar=0&navpanes=0&view=FitH&zoom=${scale * 100}`}
                  className="w-full flex-1 border border-slate-200 rounded-lg bg-white shadow-sm"
                  title="PDF Preview"
                />
              </div>
            ) : (
              <div className="flex-1 w-full flex items-center justify-center relative overflow-hidden">
                <div className="relative max-w-full max-h-full overflow-auto flex items-center justify-center p-4">
                  <img
                    src={previewModal.documentUrl}
                    alt="Document Preview"
                    className="max-w-full max-h-[65vh] object-contain rounded-lg shadow-md transition-transform duration-200"
                    style={{ transform: `scale(${scale})` }}
                  />
                </div>
                {/* Image Controls */}
                <div className="absolute bottom-4 bg-white rounded-full shadow-lg border border-slate-200 px-3 py-1.5 flex items-center gap-3">
                  <button
                    onClick={() => setScale(s => Math.max(0.5, s - 0.1))}
                    className="p-1 hover:bg-slate-100 rounded-full text-slate-600"
                  >
                    <ZoomOut className="w-4 h-4" />
                  </button>
                  <span className="text-xs font-medium text-slate-700">{Math.round(scale * 100)}%</span>
                  <button
                    onClick={() => setScale(s => Math.min(2, s + 0.1))}
                    className="p-1 hover:bg-slate-100 rounded-full text-slate-600"
                  >
                    <ZoomIn className="w-4 h-4" />
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex flex-col items-center justify-center p-4">
        <div className="relative flex flex-col items-center">
          <div className="w-12 h-12 border-4 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
          <p className="mt-4 font-medium text-slate-600 text-sm">Loading application updates...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
        <div className="max-w-md w-full bg-white rounded-xl shadow-sm border border-slate-200 p-6 text-center">
          <div className="inline-flex p-3 bg-rose-50 text-rose-600 rounded-full mb-4">
            <AlertCircle className="h-6 w-6" />
          </div>
          <h1 className="text-lg font-bold text-slate-900 mb-2">Something went wrong</h1>
          <p className="text-sm text-slate-600 mb-6">{error}</p>
          <button
            onClick={() => navigate('/signin')}
            className="w-full py-2.5 px-4 bg-blue-600 text-white font-medium text-sm rounded-lg hover:bg-blue-700 active:bg-blue-800 transition-colors shadow-sm"
          >
            Back to Sign In
          </button>
        </div>
      </div>
    );
  }

  // Common Header layout component for consistency across views
  const LayoutHeader = () => (
    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 border-b border-slate-100 pb-5 mb-6">
      <div>
        <h1 className="text-xl font-bold text-slate-900">Application Dashboard</h1>
        {application?.applicationId && (
          <p className="text-xs text-slate-500 mt-1">ID: <span className="font-mono text-slate-700">{application.applicationId}</span></p>
        )}
      </div>
      <div className={`inline-flex items-center gap-1.5 self-start sm:self-center px-3 py-1.5 rounded-full text-xs font-semibold ${getStatusColor(application.status)}`}>
        {getStatusIcon(application.status)}
        <span>{application.status}</span>
      </div>
    </div>
  );

  // Rejected View Layout
  if (application.status === 'Rejected') {
    return (
      <div className="min-h-screen bg-slate-50 py-6 px-4 md:py-12">
        {renderPreviewModal()}
        <div className="max-w-3xl mx-auto bg-white rounded-xl shadow-sm border border-slate-200 p-5 md:p-8">
          <LayoutHeader />
          
          <div className="bg-rose-50 border border-rose-200 rounded-xl p-4 mb-6 flex gap-3">
            <XCircle className="h-5 w-5 text-rose-500 flex-shrink-0 mt-0.5" />
            <div>
              <h3 className="text-sm font-semibold text-rose-900">Application Stopped</h3>
              <p className="text-xs text-rose-700 mt-1 leading-relaxed">
                Your submission was rejected by our team. Please check with customer support if you need more details.
              </p>
            </div>
          </div>

          <div className="bg-slate-50 border border-slate-100 rounded-xl p-4 md:p-6 mb-6">
            <h3 className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-4">Submitted Details</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
              <div>
                <p className="text-xs text-slate-500 mb-0.5">Organization Name</p>
                <p className="font-medium text-slate-800">{application.organizationName}</p>
              </div>
              <div>
                <p className="text-xs text-slate-500 mb-0.5">Subdomain Url</p>
                <p className="font-medium text-slate-800">{application.subdomain}.{MAIN_DOMAIN}</p>
              </div>
              <div className="md:col-span-2">
                <p className="text-xs text-slate-500 mb-0.5">Registered Address</p>
                <p className="font-medium text-slate-800">{application.address}</p>
              </div>
              <div className="md:col-span-2">
                <p className="text-xs text-slate-500 mb-0.5">Contact Line</p>
                <p className="font-medium text-slate-800">{application.phone} • {application.email}</p>
              </div>
            </div>
          </div>

          <div className="mb-8">
            <h3 className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-3">Your Documents</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {['addressProof', 'institutionProof'].map((type) => (
                <div key={type} className="flex items-center justify-between p-3.5 bg-white border border-slate-200 rounded-xl hover:bg-slate-50 transition-colors">
                  <div className="flex items-center gap-2.5 min-w-0">
                    {getFileIcon(type)}
                    <span className="text-sm text-slate-700 font-medium truncate">
                      {type === 'addressProof' ? 'Address Verification File' : 'Institution Credentials'}
                    </span>
                  </div>
                  <button
                    onClick={() => previewDocument(type)}
                    className="p-1.5 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors flex-shrink-0"
                    title="View uploaded document"
                  >
                    <Eye className="w-4 h-4" />
                  </button>
                </div>
              ))}
            </div>
          </div>

          <div className="border-t border-slate-100 pt-5 flex">
            <button
              onClick={handleLogout}
              className="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-5 py-2.5 bg-slate-800 hover:bg-slate-900 text-white rounded-lg font-medium text-sm transition-colors shadow-sm"
            >
              <LogOut className="w-4 h-4" />
              Sign Out
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Edit View Layout (For 'Review' status processing changes)
  if (application.status === 'Review' && isEditing) {
    return (
      <div className="min-h-screen bg-slate-50 py-6 px-4 md:py-12">
        {renderPreviewModal()}
        <div className="max-w-3xl mx-auto bg-white rounded-xl shadow-sm border border-slate-200 p-5 md:p-8">
          
          <div className="border-b border-slate-100 pb-4 mb-6">
            <h2 className="text-xl font-bold text-slate-900">Update Application Form</h2>
            <p className="text-xs text-slate-500 mt-1">Fix required fields below and re-submit your profile.</p>
          </div>
            
          {reviewMessage && (
            <div className="mb-6 bg-amber-50 border border-amber-200 rounded-xl p-4 flex gap-3">
              <AlertCircle className="h-5 w-5 text-amber-600 flex-shrink-0 mt-0.5" />
              <div>
                <h3 className="text-sm font-semibold text-amber-900">Notes from Reviewer</h3>
                <p className="text-xs text-amber-700 mt-1 leading-relaxed">{reviewMessage}</p>
              </div>
            </div>
          )}

          <div className="space-y-5">
            <div>
              <label className="block text-xs font-semibold text-slate-700 mb-1">
                Organization Name <span className="text-rose-500">*</span>
              </label>
              <input
                type="text"
                name="organizationName"
                value={editForm.organizationName}
                onChange={handleEditChange}
                className={`w-full text-sm px-3.5 py-2.5 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500/20 transition-all ${
                  errors.organizationName ? 'border-rose-300 bg-rose-50/20 focus:border-rose-500' : 'border-slate-200 focus:border-blue-500'
                }`}
              />
              {errors.organizationName && <p className="mt-1 text-xs text-rose-600">{errors.organizationName}</p>}
            </div>

            <div>
              <label className="block text-xs font-semibold text-slate-700 mb-1">
                Physical Address <span className="text-rose-500">*</span>
              </label>
              <input
                type="text"
                name="address"
                value={editForm.address}
                onChange={handleEditChange}
                className={`w-full text-sm px-3.5 py-2.5 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500/20 transition-all ${
                  errors.address ? 'border-rose-300 bg-rose-50/20 focus:border-rose-500' : 'border-slate-200 focus:border-blue-500'
                }`}
              />
              {errors.address && <p className="mt-1 text-xs text-rose-600">{errors.address}</p>}
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-semibold text-slate-700 mb-1">Telephone Number</label>
                <input
                  type="tel"
                  name="phone"
                  value={editForm.phone}
                  onChange={handleEditChange}
                  className="w-full text-sm px-3.5 py-2.5 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all"
                />
              </div>
              <div>
                <label className="block text-xs font-semibold text-slate-700 mb-1">
                  Email Address <span className="text-rose-500">*</span>
                </label>
                <input
                  type="email"
                  name="email"
                  value={editForm.email}
                  onChange={handleEditChange}
                  className={`w-full text-sm px-3.5 py-2.5 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500/20 transition-all ${
                    errors.email ? 'border-rose-300 bg-rose-50/20 focus:border-rose-500' : 'border-slate-200 focus:border-blue-500'
                  }`}
                />
                {errors.email && <p className="mt-1 text-xs text-rose-600">{errors.email}</p>}
              </div>
            </div>

            {/* Document Resubmission Section */}
            <div className="border-t border-slate-100 pt-6 mt-6">
              <h3 className="text-sm font-bold text-slate-800 mb-4">Verification Files</h3>
              
              {[
                { type: 'addressProof', label: 'Address Verification Proof', color: 'blue' },
                { type: 'institutionProof', label: 'Institution Status Proof', color: 'purple' }
              ].map(({ type, label, color }) => (
                <div key={type} className="mb-5 p-4 bg-slate-50/80 rounded-xl border border-slate-200">
                  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 mb-3">
                    <h4 className="text-xs font-bold text-slate-700 flex items-center gap-2">
                      {getFileIcon(type)}
                      {label}
                    </h4>
                    <button
                      type="button"
                      onClick={() => toggleReplaceDocument(type)}
                      className={`text-xs font-semibold inline-flex items-center gap-1 self-start sm:self-center transition-colors ${
                        !replaceDocuments[type] ? 'text-blue-600 hover:text-blue-700' : 'text-slate-500 hover:text-slate-700'
                      }`}
                    >
                      {!replaceDocuments[type] ? (
                        <>
                          <Upload className="w-3.5 h-3.5" />
                          Upload Replacement
                        </>
                      ) : 'Keep Original'}
                    </button>
                  </div>

                  <div className="flex items-center justify-between p-3 bg-white border border-slate-200 rounded-lg shadow-2xs">
                    <div className="flex items-center min-w-0 gap-2">
                      <FileText className="w-4 h-4 text-slate-400 flex-shrink-0" />
                      <span className="text-xs font-medium text-slate-600 truncate">
                        {application.documents?.[type]?.fileName || 'Current uploaded file'}
                      </span>
                      {application.documents?.[type]?.fileSize && (
                        <span className="text-[10px] text-slate-400 flex-shrink-0">
                          ({(application.documents[type].fileSize / 1024 / 1024).toFixed(2)} MB)
                        </span>
                      )}
                    </div>
                    <button
                      onClick={() => previewDocument(type)}
                      className="p-1 text-slate-500 hover:text-blue-600 transition-colors"
                      title="Preview this file"
                    >
                      <Eye className="w-4 h-4" />
                    </button>
                  </div>

                  {replaceDocuments[type] && (
                    <div className="mt-3">
                      <input
                        type="file"
                        id={type}
                        accept=".jpg,.jpeg,.png,.gif,.webp,.pdf"
                        onChange={(e) => handleFileChange(e, type)}
                        className="hidden"
                      />
                      {!newFiles[type] ? (
                        <label
                          htmlFor={type}
                          className={`flex flex-col items-center justify-center w-full p-4 border-2 border-dashed rounded-lg cursor-pointer transition-all bg-white hover:bg-slate-50 ${
                            color === 'blue' ? 'border-blue-200 hover:border-blue-400' : 'border-purple-200 hover:border-purple-400'
                          }`}
                        >
                          <Upload className={`w-5 h-5 mb-1 ${color === 'blue' ? 'text-blue-400' : 'text-purple-400'}`} />
                          <span className="text-xs text-slate-600 font-medium">Click to select new file</span>
                          <span className="text-[10px] text-slate-400 mt-0.5">Images or PDF up to 5MB</span>
                        </label>
                      ) : (
                        <div className={`flex items-center justify-between p-3 border rounded-lg ${
                          color === 'blue' ? 'bg-blue-50/50 border-blue-200' : 'bg-purple-50/50 border-purple-200'
                        }`}>
                          <div className="flex items-center min-w-0 gap-2">
                            <FileText className={`w-4 h-4 flex-shrink-0 ${color === 'blue' ? 'text-blue-500' : 'text-purple-500'}`} />
                            <span className="text-xs text-slate-700 font-medium truncate">{newFiles[type].name}</span>
                            <span className="text-[10px] text-slate-400 flex-shrink-0">
                              ({(newFiles[type].size / 1024 / 1024).toFixed(2)} MB)
                            </span>
                          </div>
                          <button
                            onClick={() => removeNewFile(type)}
                            className="p-1 text-rose-500 hover:bg-rose-50 rounded transition-colors"
                          >
                            <X className="w-4 h-4" />
                          </button>
                        </div>
                      )}
                      {errors[type] && <p className="mt-1 text-xs text-rose-600">{errors[type]}</p>}
                    </div>
                  )}
                </div>
              ))}
            </div>

            {/* Action Panel */}
            <div className="flex flex-col sm:flex-row justify-end gap-3 pt-4 border-t border-slate-100">
              <button
                onClick={() => {
                  setIsEditing(false);
                  setReplaceDocuments({ addressProof: false, institutionProof: false });
                  setNewFiles({ addressProof: null, institutionProof: null });
                  setFilePreviews({ addressProof: null, institutionProof: null });
                }}
                className="w-full sm:w-auto px-5 py-2.5 border border-slate-200 hover:bg-slate-50 text-slate-700 rounded-lg text-sm font-semibold transition-colors"
              >
                Cancel Changes
              </button>
              <button
                onClick={handleSubmitEdit}
                disabled={isSubmitting}
                className="w-full sm:w-auto px-5 py-2.5 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm font-semibold shadow-sm transition-colors disabled:opacity-60"
              >
                {isSubmitting ? 'Saving changes...' : 'Submit updates'}
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Default Standard View (Pending, Approved, or Review without interactive editing state)
  return (
    <div className="min-h-screen bg-slate-50 py-6 px-4 md:py-12">
      {renderPreviewModal()}
      <div className="max-w-3xl mx-auto bg-white rounded-xl shadow-sm border border-slate-200 p-5 md:p-8">
        
        <LayoutHeader />
        
        <div className="space-y-6">
          {/* Status Banners */}
          {application.status === 'Review' && application.reviewComments && (
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 flex gap-3">
              <AlertCircle className="h-5 w-5 text-amber-600 flex-shrink-0 mt-0.5" />
              <div>
                <h3 className="text-sm font-semibold text-amber-900">Action Required</h3>
                <p className="text-xs text-amber-700 mt-1 leading-relaxed">{application.reviewComments}</p>
              </div>
            </div>
          )}

          {application.status === 'Approved' && (
            <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-4 flex gap-3">
              <CheckCircle className="h-5 w-5 text-emerald-600 flex-shrink-0 mt-0.5" />
              <div className="min-w-0 flex-1">
                <h3 className="text-sm font-semibold text-emerald-900">Application Approved!</h3>
                <p className="text-xs text-emerald-700 mt-1 leading-relaxed">
                  Your organization account is fully activated. You can access your secure workspace at:
                </p>
                <a
                  href={`https://${application.subdomain}.${MAIN_DOMAIN}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="mt-2 inline-block text-xs font-semibold text-blue-600 hover:text-blue-800 underline truncate max-w-full"
                >
                  https://{application.subdomain}.{MAIN_DOMAIN}
                </a>
              </div>
            </div>
          )}

          {/* Form details overview card */}
          <div className="bg-slate-50 border border-slate-100 rounded-xl p-4 md:p-6">
            <h3 className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-4">Application Details</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
              <div>
                <p className="text-xs text-slate-500 mb-0.5">Organization Name</p>
                <p className="font-medium text-slate-800">{application.organizationName}</p>
              </div>
              <div>
                <p className="text-xs text-slate-500 mb-0.5">Assigned Subdomain</p>
                <p className="font-medium text-slate-800">
                  {application.status === 'Approved' ? (
                    <a
                      href={`https://${application.subdomain}.${MAIN_DOMAIN}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-blue-600 hover:text-blue-800 underline"
                    >
                      {application.subdomain}.{MAIN_DOMAIN}
                    </a>
                  ) : (
                    <span>{application.subdomain}.{MAIN_DOMAIN}</span>
                  )}
                </p>
              </div>
              <div className="md:col-span-2">
                <p className="text-xs text-slate-500 mb-0.5">Registered Address</p>
                <p className="font-medium text-slate-800">{application.address}</p>
              </div>
              <div className="md:col-span-2">
                <p className="text-xs text-slate-500 mb-0.5">Contact Line</p>
                <p className="font-medium text-slate-800">{application.phone} • {application.email}</p>
              </div>
            </div>
          </div>

          {/* Document Section */}
          <div>
            <h3 className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-3">Uploaded Verification Records</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {[
                { key: 'addressProof', name: 'Address Proof File' },
                { key: 'institutionProof', name: 'Institution Credentials' }
              ].map(({ key, name }) => (
                <div key={key} className="flex items-center justify-between p-3.5 bg-white border border-slate-200 rounded-xl hover:bg-slate-50 transition-colors">
                  <div className="flex items-center min-w-0 gap-2">
                    {getFileIcon(key)}
                    <div className="min-w-0">
                      <p className="text-sm font-medium text-slate-700 truncate">{name}</p>
                      {application.documents?.[key]?.fileSize && (
                        <p className="text-[10px] text-slate-400 mt-0.5">
                          {(application.documents[key].fileSize / 1024 / 1024).toFixed(2)} MB
                        </p>
                      )}
                    </div>
                  </div>
                  <button
                    onClick={() => previewDocument(key)}
                    className="p-1.5 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors flex-shrink-0"
                    title="View uploaded asset"
                  >
                    <Eye className="w-4 h-4" />
                  </button>
                </div>
              ))}
            </div>
          </div>

          {/* Navigation Action Panel */}
          <div className="border-t border-slate-100 pt-5 flex flex-col sm:flex-row justify-between items-center gap-3">
            <button
              onClick={handleLogout}
              className="w-full sm:w-auto px-4 py-2.5 text-slate-600 hover:text-slate-900 hover:bg-slate-100 font-semibold text-sm rounded-lg text-center transition-colors"
            >
              Sign Out
            </button>
            
            {application.status === 'Review' && (
              <button
                onClick={() => setIsEditing(true)}
                className="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-5 py-2.5 bg-blue-600 hover:bg-blue-700 text-white font-semibold text-sm rounded-lg transition-colors shadow-sm"
              >
                <Edit className="w-4 h-4" />
                Modify Profile
              </button>
            )}
          </div>

        </div>
      </div>
    </div>
  );
};

export default ApplicationStatus;